import { getChannelGroupByChannelType } from '../channels/channelCatalog'
import { CHANNEL_TYPES } from '../../vars/general'
import { getFirstFreeSlotId, hasAvailableChannelSlots } from '@/helpers/channels/channelSlots'

export const OPEN_CHANNEL_CREATION_QUERY = 'openChannelCreation'
export const OPEN_ADD_CHANNEL_BILLING_QUERY = 'openAddChannelBilling'

type MailingConnector = {
    connectorId?: string
    id?: string
    connectorType?: string
    phone?: string | null
    isChisai?: boolean
}

export type MailingProject = {
    id?: string
    isOnBoardingActive?: boolean
    billingEnabled?: boolean
    isChannelsSlots?: boolean
    isChannelsRework?: boolean
    connectors?: MailingConnector[]
}

type SlotLike = {
    id?: string
    channelConnectorId?: string | null
    isTariff?: boolean | number | string
    type?: string | null
}

export type ConnectMailingChannelAction =
    | { kind: 'onboarding' }
    | { kind: 'openConnector'; connectorId: string }
    | { kind: 'createChannel'; group: string; slotId: string | null }
    | { kind: 'buySlot' }

const CHANNEL_TYPE_VALUES = Object.values(CHANNEL_TYPES)

function getConnectorId(connector: MailingConnector | null | undefined): string | null {
    return connector?.connectorId || connector?.id || null
}

export function connectorHasPhone(connector: MailingConnector | null | undefined): boolean {
    const phone = connector?.phone
    return phone != null && String(phone).trim() !== ''
}

export function isMailingChannelConnector(connector: MailingConnector | null | undefined): boolean {
    if (!connector || connector.isChisai) return false
    return CHANNEL_TYPE_VALUES.includes(connector.connectorType as (typeof CHANNEL_TYPE_VALUES)[number])
}

export function getMailingChannelConnectors(project: MailingProject | null | undefined): MailingConnector[] {
    return (project?.connectors || []).filter(isMailingChannelConnector)
}

export function isChannelFlowProject(project: MailingProject | null | undefined): boolean {
    return Boolean(project?.isChannelsSlots && project?.isChannelsRework)
}

export function getChannelFlowProjects(
    projects: Array<MailingProject | null | undefined> | null | undefined
): MailingProject[] {
    return (projects || []).filter(isChannelFlowProject) as MailingProject[]
}

export function hasAnyMailingChannelWithPhone(
    projects: Array<MailingProject | null | undefined> | null | undefined
): boolean {
    return (projects || []).some((project) =>
        getMailingChannelConnectors(project).some((connector) => connectorHasPhone(connector))
    )
}

export function getMailingConnectorsNeedingPhoneLookup(
    projects: Array<MailingProject | null | undefined> | null | undefined
): Array<MailingConnector & { connectorId: string }> {
    const result: Array<MailingConnector & { connectorId: string }> = []
    for (const project of projects || []) {
        for (const connector of getMailingChannelConnectors(project)) {
            if (connectorHasPhone(connector)) continue
            const connectorId = getConnectorId(connector)
            if (!connectorId) continue
            result.push({ ...connector, connectorId })
        }
    }
    return result
}

export function shouldShowChannelsBanner(
    projects: Array<MailingProject | null | undefined> | null | undefined
): boolean {
    const eligible = getChannelFlowProjects(projects)
    return eligible.length > 0 && !hasAnyMailingChannelWithPhone(projects)
}

export function mailingChannelLabel(channelType?: string | null): string {
    if (channelType === CHANNEL_TYPES.maxChannel) return 'MAX'
    if (channelType === CHANNEL_TYPES.telegramChannel) return 'Telegram'
    return 'канал'
}

function usesChannelSlots(project: MailingProject | null | undefined): boolean {
    return Boolean(project?.billingEnabled && project?.isChannelsSlots)
}

export function resolveConnectMailingChannelAction({
    project,
    channelType,
    slots,
}: {
    project: MailingProject | null | undefined
    channelType: string
    slots?: SlotLike[] | null
}): ConnectMailingChannelAction {
    if (project?.isOnBoardingActive) {
        return { kind: 'onboarding' }
    }

    const channels = getMailingChannelConnectors(project)
    const sameTypeWithoutPhone = channels.find(
        (connector) => connector.connectorType === channelType && !connectorHasPhone(connector)
    )
    const sameTypeId = getConnectorId(sameTypeWithoutPhone)
    if (sameTypeId) {
        return { kind: 'openConnector', connectorId: sameTypeId }
    }

    const group = getChannelGroupByChannelType(channelType) || ''
    const slotList = slots || []
    const hasFreeSlot = hasAvailableChannelSlots(slotList)
    const slotId = hasFreeSlot ? getFirstFreeSlotId(slotList) : null
    const noChannels = channels.length === 0
    const hasDifferentTypeWithoutPhone = channels.some(
        (connector) => connector.connectorType !== channelType && !connectorHasPhone(connector)
    )

    if (!usesChannelSlots(project)) {
        return { kind: 'createChannel', group, slotId }
    }

    if ((noChannels || hasDifferentTypeWithoutPhone) && hasFreeSlot) {
        return { kind: 'createChannel', group, slotId }
    }

    if (hasFreeSlot) {
        return { kind: 'createChannel', group, slotId }
    }

    return { kind: 'buySlot' }
}

export function isTruthyQueryValue(value: unknown): boolean {
    return value === '1' || value === 'true' || value === true || value === 1
}
